import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UtensilsCrossed, ShoppingCart, Refrigerator, CalendarDays, PiggyBank, LifeBuoy, Users, ChefHat, Settings as Cog, ChevronRight, Star, Brain } from "lucide-react";
import BottomNav from "../components/BottomNav";
import { SpeechBubble, MillieHero } from "../components/MascotBubble";
import MillieChat from "../components/MillieChat";

export default function AskMillie() {
  const navigate = useNavigate();
  const rows = [
    { icon: UtensilsCrossed, title: "Plan my meals", sub: "Create or edit your meal plan", bg: "bg-[#FCE1E8]", ic: "text-sorted-rose", to: "/meals", testId: "hub-meals" },
    { icon: ShoppingCart, title: "My shopping list", sub: "View, edit or add items", bg: "bg-[#DDEBE1]", ic: "text-emerald-600", to: "/shopping", testId: "hub-shopping" },
    { icon: Refrigerator, title: "My kitchen", sub: "Scan, view or edit your inventory", bg: "bg-[#FBE6DC]", ic: "text-orange-500", to: "/kitchen", testId: "hub-kitchen" },
    { icon: CalendarDays, title: "My calendar", sub: "Busy nights from Google or a calendar link", bg: "bg-[#DEE7F5]", ic: "text-blue-500", to: "/profile", testId: "hub-calendar" },
    { icon: PiggyBank, title: "My budget", sub: "Set or adjust your weekly budget", bg: "bg-[#FAD9CE]", ic: "text-warning", to: "/profile", testId: "hub-budget" },
    { icon: LifeBuoy, title: "SOS Dinner", sub: "I need help right now!", bg: "bg-[#FAD9E1]", ic: "text-sorted-rose", to: "/sos", testId: "hub-sos" },
    { icon: Users, title: "My household", sub: "People, pets, preferences & family sharing", bg: "bg-[#FCEFCF]", ic: "text-amber-500", to: "/profile", testId: "hub-household" },
    { icon: ChefHat, title: "Recipes", sub: "Browse this week's recipes", bg: "bg-[#DDEBE1]", ic: "text-emerald-600", to: "/recipes", testId: "hub-recipes" },
    { icon: Brain, title: "What Millie remembers", sub: "Favourites, rituals, busy nights & habits", bg: "bg-[#DEE7F5]", ic: "text-blue-500", to: "/memory", testId: "hub-memory" },
    { icon: Star, title: "Little Wins", sub: "Fussy eater progress, no pressure", bg: "bg-[#FCEFCF]", ic: "text-amber-500", to: "/wins", testId: "hub-wins" },
    { icon: Cog, title: "Settings & preferences", sub: "AI model, accessibility, theme", bg: "bg-[#E7E0F3]", ic: "text-purple-500", to: "/settings", testId: "hub-settings" },
  ];

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#FCE7EC] pb-28">
        <div className="px-6 pt-12">
          <h1 className="font-heading text-3xl font-bold text-sorted-ink leading-tight">What would you<br />like to do?</h1>
          <p className="text-sorted-slate font-semibold mt-1">I'm here to help with whatever you need.</p>
        </div>

        <div className="relative h-52 mt-1">
          <MillieHero mood="point" entrance="wave" className="absolute right-0 bottom-0 h-52 w-[48%]" testId="hub-millie-hero" />
          <div className="absolute left-6 top-3 max-w-[50%]">
            <SpeechBubble tail="br" testId="hub-millie-bubble">Type it or say it — "what can I make with mince?" — and I'll sort it. 👍</SpeechBubble>
          </div>
        </div>

        <div className="px-5 -mt-2">
          <MillieChat />
        </div>

        <p className="px-6 mt-8 mb-2 font-hand text-2xl text-sorted-ink">Or jump straight to…</p>
        <div className="px-5 space-y-2.5">
          {rows.map((r, i) => (
            <motion.button
              key={r.title}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => (r.to ? navigate(r.to) : r.action?.())}
              data-testid={r.testId}
              className="w-full bg-white rounded-3xl border-2 border-[#FDECEF] shadow-soft p-3 flex items-center gap-3 text-left"
            >
              <span className={`w-12 h-12 rounded-2xl ${r.bg} flex items-center justify-center ${r.ic}`}>
                <r.icon size={22} strokeWidth={2.5} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="font-heading font-semibold text-sorted-ink block leading-tight">{r.title}</span>
                <span className="text-xs text-sorted-slate font-semibold">{r.sub}</span>
              </span>
              <ChevronRight size={20} strokeWidth={2.5} className="text-sorted-slate shrink-0" />
            </motion.button>
          ))}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
